import { Controller } from "@hotwired/stimulus";

/**
 * Stamp Balance Controller
 * - Shows animated stamp balance changes with count-up/count-down
 * - Handles the dropdown (stamp history link); closes on turbo:before-cache, Escape, focus out
 * - Listens for custom events to update balance with animation
 * - Queues rapid changes and handles them gracefully
 *
 * Usage:
 *   <div data-controller="stamp-balance" data-stamp-balance-current-value="42">
 *     <button data-action="click->stamp-balance#toggle">
 *       <span data-stamp-balance-target="icon"><svg class="stamp-icon">…</svg></span>
 *       <span data-stamp-balance-target="amount">42</span>
 *     </button>
 *     <span data-stamp-balance-target="badge"></span>
 *     <div data-stamp-balance-target="menu" class="hidden">...</div>
 *   </div>
 *
 * Events:
 *   window.dispatchEvent(new CustomEvent('stamps:changed', {
 *     detail: { amount: 5, type: 'earn', newBalance: 47, reason: 'belt_completion' }
 *   }))
 */

interface StampChangeDetail {
  amount: number;
  type: "earn" | "spend";
  newBalance?: number;
  reason?: string;
}

interface QueuedChange {
  amount: number;
  type: "earn" | "spend";
  targetBalance: number;
}

// Animation timing constants (in ms)
const TIMING = {
  ICON_ANIMATION: 400,
  BADGE_SHOW_DELAY: 100,
  COUNT_START_DELAY: 200,
  COUNT_DURATION: 400,
  BADGE_VISIBLE_DURATION: 2000,
  BADGE_EXIT_DURATION: 300,
  TOTAL_EARN: 2500,
  TOTAL_SPEND: 2000,
} as const;

export default class extends Controller<HTMLElement> {
  static targets = ["trigger", "amount", "menuAmount", "menu", "badge", "icon"];
  static values = {
    current: Number,
  };

  declare readonly triggerTarget: HTMLElement;
  declare readonly amountTarget: HTMLElement;
  declare readonly menuAmountTarget: HTMLElement;
  declare readonly hasMenuAmountTarget: boolean;
  declare readonly menuTarget: HTMLElement;
  declare readonly badgeTarget: HTMLElement;
  declare readonly iconTarget: HTMLElement;
  declare readonly hasMenuTarget: boolean;
  declare readonly hasBadgeTarget: boolean;
  declare readonly hasIconTarget: boolean;
  declare currentValue: number;

  private isOpen = false;
  private boundHandleClickOutside!: (event: MouseEvent) => void;
  private boundHandleEscape!: (event: KeyboardEvent) => void;
  private boundHandleStampChange!: (event: Event) => void;
  private boundHandleCloseOthers!: (event: Event) => void;
  private boundHandleVisibilityChange!: () => void;
  private boundHandleFocusOut!: (event: FocusEvent) => void;
  private boundClose!: () => void;

  // Animation state
  private isAnimating = false;
  private changeQueue: QueuedChange[] = [];
  private animationTimeouts: number[] = [];
  private countAnimationFrame: number | null = null;

  // Tab visibility handling
  private pendingChanges: QueuedChange[] = [];
  private wasTabHidden = false;

  connect() {
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.boundHandleEscape = this.handleEscape.bind(this);
    this.boundHandleStampChange = ((event: CustomEvent<StampChangeDetail>) => {
      this.handleStampChange(event);
    }) as EventListener;
    this.boundHandleCloseOthers = this.handleCloseOthers.bind(this);
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.boundHandleFocusOut = this.handleFocusOut.bind(this);
    this.boundClose = this.close.bind(this);

    // Listen for stamp change events
    window.addEventListener("stamps:changed", this.boundHandleStampChange);

    // Listen for close-other-dropdowns event
    window.addEventListener("dropdown:close-others", this.boundHandleCloseOthers);

    // Listen for tab visibility changes
    document.addEventListener("visibilitychange", this.boundHandleVisibilityChange);

    // Turbo snapshots the page as it is; without this Back/Forward restores the menu open
    // while the controller thinks it is closed
    document.addEventListener("turbo:before-cache", this.boundClose);
    this.element.addEventListener("focusout", this.boundHandleFocusOut);
    this.close();
  }

  disconnect() {
    this.removeEventListeners();
    window.removeEventListener("stamps:changed", this.boundHandleStampChange);
    window.removeEventListener("dropdown:close-others", this.boundHandleCloseOthers);
    document.removeEventListener("visibilitychange", this.boundHandleVisibilityChange);
    document.removeEventListener("turbo:before-cache", this.boundClose);
    this.element.removeEventListener("focusout", this.boundHandleFocusOut);
    this.clearAllTimeouts();
    if (this.countAnimationFrame) {
      cancelAnimationFrame(this.countAnimationFrame);
    }
  }

  private handleCloseOthers(event: Event) {
    const customEvent = event as CustomEvent;
    // Close this dropdown if it's not the one that triggered the event
    if (customEvent.detail?.source !== this.element && this.isOpen) {
      this.close();
    }
  }

  // Dropdown methods
  toggle(event: Event) {
    event.preventDefault();

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this.hasMenuTarget) return;

    // Emit event to close other dropdowns
    window.dispatchEvent(
      new CustomEvent("dropdown:close-others", { detail: { source: this.element } })
    );

    this.isOpen = true;
    this.menuTarget.classList.remove("hidden");
    this.triggerTarget.setAttribute("aria-expanded", "true");

    document.addEventListener("click", this.boundHandleClickOutside);
    document.addEventListener("keydown", this.boundHandleEscape);
  }

  close() {
    if (!this.hasMenuTarget) return;

    this.isOpen = false;
    this.menuTarget.classList.add("hidden");
    this.triggerTarget.setAttribute("aria-expanded", "false");

    this.removeEventListeners();
  }

  closeOnClick() {
    this.close();
  }

  private handleClickOutside(event: MouseEvent) {
    const target = event.target as Node;
    if (!this.element.contains(target)) {
      this.close();
    }
  }

  private handleEscape(event: KeyboardEvent) {
    if (event.key === "Escape") {
      this.close();
      this.triggerTarget.focus();
    }
  }

  private handleFocusOut(event: FocusEvent) {
    const next = event.relatedTarget as Node | null;
    // relatedTarget is null for clicks on non-focusable content - click outside handles those
    if (next && !this.element.contains(next)) this.close();
  }

  private removeEventListeners() {
    document.removeEventListener("click", this.boundHandleClickOutside);
    document.removeEventListener("keydown", this.boundHandleEscape);
  }

  // Tab visibility handling
  private handleVisibilityChange() {
    if (document.hidden) {
      this.wasTabHidden = true;
    } else if (this.wasTabHidden && this.pendingChanges.length > 0) {
      // Tab became visible and we have pending changes
      this.processPendingChanges();
      this.wasTabHidden = false;
    }
  }

  private processPendingChanges() {
    if (this.pendingChanges.length === 0) return;

    // Sum up all pending changes
    let totalEarn = 0;
    let totalSpend = 0;
    let finalBalance = this.currentValue;

    for (const change of this.pendingChanges) {
      if (change.type === "earn") {
        totalEarn += change.amount;
      } else {
        totalSpend += change.amount;
      }
      finalBalance = change.targetBalance;
    }

    this.pendingChanges = [];

    // Show net change
    const netChange = totalEarn - totalSpend;
    if (netChange !== 0) {
      const type = netChange > 0 ? "earn" : "spend";
      this.queueChange({
        amount: Math.abs(netChange),
        type,
        targetBalance: finalBalance,
      });
    }
  }

  // Stamp change handling
  private handleStampChange(event: CustomEvent<StampChangeDetail>) {
    const { amount, type, newBalance } = event.detail;

    // Calculate target balance
    let targetBalance: number;
    if (newBalance !== undefined) {
      targetBalance = newBalance;
    } else {
      targetBalance = type === "earn" ? this.currentValue + amount : this.currentValue - amount;
    }

    const change: QueuedChange = {
      amount,
      type,
      targetBalance,
    };

    // If tab is hidden, store for later
    if (document.hidden) {
      this.pendingChanges.push(change);
      // Still update the internal balance
      this.currentValue = targetBalance;
      this.showBalance(targetBalance);
      return;
    }

    this.queueChange(change);
  }

  private queueChange(change: QueuedChange) {
    if (this.isAnimating) {
      // If already animating, interrupt and start new animation
      this.interruptCurrentAnimation();
      // Update to latest balance immediately
      this.currentValue = change.targetBalance;
      this.showBalance(change.targetBalance);
    }

    this.changeQueue.push(change);
    this.processQueue();
  }

  private processQueue() {
    if (this.isAnimating || this.changeQueue.length === 0) return;

    const change = this.changeQueue.shift()!;
    this.animateChange(change);
  }

  private interruptCurrentAnimation() {
    this.clearAllTimeouts();
    if (this.countAnimationFrame) {
      cancelAnimationFrame(this.countAnimationFrame);
      this.countAnimationFrame = null;
    }

    // Clean up badge immediately
    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.add("hidden");
      this.badgeTarget.classList.remove("stamp-badge-enter", "stamp-badge-exit");
    }

    // Clean up icon animations
    if (this.hasIconTarget) {
      this.iconTarget.classList.remove("stamp-pulse", "stamp-shake");
    }

    this.amountTarget.classList.remove("stamp-count-animate");
    this.isAnimating = false;
  }

  private clearAllTimeouts() {
    for (const timeout of this.animationTimeouts) {
      clearTimeout(timeout);
    }
    this.animationTimeouts = [];
  }

  private addTimeout(callback: () => void, delay: number): void {
    const timeout = window.setTimeout(callback, delay);
    this.animationTimeouts.push(timeout);
  }

  private animateChange(change: QueuedChange) {
    const { amount, type, targetBalance } = change;
    const startBalance = this.currentValue;

    this.isAnimating = true;

    // 1. Icon animation (T+0ms)
    if (this.hasIconTarget) {
      const animationClass = type === "earn" ? "stamp-pulse" : "stamp-shake";
      this.iconTarget.classList.add(animationClass);

      this.addTimeout(() => {
        this.iconTarget.classList.remove(animationClass);
      }, TIMING.ICON_ANIMATION);
    }

    // 2. Show floating badge (T+100ms)
    if (this.hasBadgeTarget) {
      this.addTimeout(() => {
        this.showBadge(amount, type);
      }, TIMING.BADGE_SHOW_DELAY);
    }

    // 3. Count animation (T+200ms)
    this.addTimeout(() => {
      this.animateCount(startBalance, targetBalance, type);
    }, TIMING.COUNT_START_DELAY);

    // 4. Badge fade out (T+2000ms for earn, T+1500ms for spend)
    const badgeFadeDelay = type === "earn" ? TIMING.BADGE_VISIBLE_DURATION : 1500;
    this.addTimeout(() => {
      this.hideBadge();
    }, badgeFadeDelay);

    // 5. Animation complete
    const totalDuration = type === "earn" ? TIMING.TOTAL_EARN : TIMING.TOTAL_SPEND;
    this.addTimeout(() => {
      this.isAnimating = false;
      this.processQueue(); // Process next in queue if any
    }, totalDuration);
  }

  private showBadge(amount: number, type: "earn" | "spend") {
    if (!this.hasBadgeTarget) return;

    const sign = type === "earn" ? "+" : "-";
    const colorClass = type === "earn" ? "stamp-badge-earn" : "stamp-badge-spend";

    // Set content and styles
    this.badgeTarget.textContent = `${sign}${amount}`;
    this.badgeTarget.className = `stamp-badge ${colorClass} stamp-badge-enter`;
  }

  private hideBadge() {
    if (!this.hasBadgeTarget) return;

    // Switch to exit animation
    this.badgeTarget.classList.remove("stamp-badge-enter");
    this.badgeTarget.classList.add("stamp-badge-exit");

    // Hide after exit animation completes
    this.addTimeout(() => {
      this.badgeTarget.classList.add("hidden");
      this.badgeTarget.classList.remove("stamp-badge-exit", "stamp-badge-earn", "stamp-badge-spend");
    }, TIMING.BADGE_EXIT_DURATION);
  }

  private animateCount(from: number, to: number, type: "earn" | "spend") {
    const startTime = performance.now();
    const duration = TIMING.COUNT_DURATION;
    const diff = to - from;

    // Add glow effect
    this.amountTarget.classList.add("stamp-count-animate");
    if (type === "earn") {
      this.amountTarget.classList.add("text-success");
    } else {
      this.amountTarget.classList.add("text-error");
    }

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(from + diff * eased);

      this.showBalance(current);

      if (progress < 1) {
        this.countAnimationFrame = requestAnimationFrame(animate);
      } else {
        // Ensure we land on the exact target
        this.showBalance(to);
        this.currentValue = to;
        this.countAnimationFrame = null;

        // Remove color class after a short delay
        this.addTimeout(() => {
          this.amountTarget.classList.remove("stamp-count-animate", "text-success", "text-error");
        }, 200);
      }
    };

    this.countAnimationFrame = requestAnimationFrame(animate);
  }

  // Balance in the navbar chip and the dropdown header, with thousands separators (1,078)
  private showBalance(value: number) {
    const text = value.toLocaleString("en-US");
    this.amountTarget.textContent = text;
    if (this.hasMenuAmountTarget) this.menuAmountTarget.textContent = text;
  }

  // Public methods for external triggering
  animateEarn(amount: number, newBalance?: number) {
    window.dispatchEvent(
      new CustomEvent("stamps:changed", {
        detail: { amount, type: "earn", newBalance },
      })
    );
  }

  animateSpend(amount: number, newBalance?: number) {
    window.dispatchEvent(
      new CustomEvent("stamps:changed", {
        detail: { amount, type: "spend", newBalance },
      })
    );
  }
}
